import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { 
  ShieldCheck, Globe, Zap, 
  ArrowUpRight, HardDrive, 
  Activity, Loader2, ChevronDown, 
  FileText, ChevronRight, FileDown
} from 'lucide-react';

/**
 * EVALNDT AUTONOMOUS ENGINE - SYSTEM INSIGHTS BILLING PORTAL
 * Refactored for Rigid Industrial Layout
 */
const EvalNDTBillingPortal = ({ profile, stats, currentUser, loading }) => {
  // --- 1. DYNAMIC SYSTEM STATE ---
  const [isIndia, setIsIndia] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState('in_project');
  const [isLoading, setIsLoading] = useState(true);
  const [availableCredits, setAvailableCredits] = useState(0);
  const [region, setRegion] = useState('India');
  const [viewYear, setViewYear] = useState('2026');
  const [viewMonth, setViewMonth] = useState('April');

  // --- 2. DATABASE INTEGRATION ---
  useEffect(() => {
    const fetchCredits = async () => {
      setIsLoading(true);
      try {
        const credits_remaining = profile?.credits_remaining || 0; 
        setAvailableCredits(credits_remaining);
      } catch (error) {
        console.error("Database sync failed", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCredits();
  }, [profile]);

  const regions = [
    { name: 'India', currency: 'INR', symbol: '₹' },
    { name: 'United States', currency: 'USD', symbol: '$' },
    { name: 'European Union', currency: 'EUR', symbol: '€' },
    { name: 'Middle East', currency: 'AED', symbol: 'د.إ' }
  ];

  const billingEngine = {
    domestic: {
      currency: "INR", symbol: "₹",
      tiers: [
        { id: 'in_nano', name: "Nano-Entry", credits: 10, price: 100 },
        { id: 'in_micro', name: "Micro-Standard", credits: 50, price: 500 },
        { id: 'in_starter', name: "Industrial Starter", credits: 100, price: 1000 },
        { id: 'in_project', name: "Project Deployment", credits: 500, price: 5000, popular: true },
        { id: 'in_enterprise', name: "Enterprise Logic", credits: 2000, price: 20000 },
      ]
    },
    global: {
      currency: "USD", symbol: "$",
      tiers: [
        { id: 'gl_nano', name: "Global Nano", credits: 10, price: 10 },
        { id: 'gl_micro', name: "Global Micro", credits: 50, price: 50 },
        { id: 'gl_lite', name: "Global Lite", credits: 100, price: 100 },
        { id: 'gl_pro', name: "Global Pro", credits: 500, price: 500, popular: true },
        { id: 'gl_ent', name: "Global Enterprise", credits: 2000, price: 2000 },
      ]
    }
  };

  const currentTiers = isIndia ? billingEngine.domestic.tiers : billingEngine.global.tiers;
  const currentSymbol = isIndia ? "₹" : "$";
  const selectedPlan = currentTiers.find(t => t.id === selectedPlanId);
  const projectedTotal = availableCredits + (selectedPlan ? selectedPlan.credits : 0);
  const operatorShortID = currentUser?.id?.slice(-5).toUpperCase() || "ADMIN";

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col p-0 md:p-4">
                  <div className="flex-1 flex flex-col bg-white border border-slate-300 shadow-2xl overflow-hidden">

      {/* TOP LEVEL: STATUTORY HEADER */}
      <div className="flex flex-col md:flex-row items-stretch justify-between border-b border-slate-300">
        
        {/* OPERATIONAL IDENTITY */}
        <div className="p-8 border-r border-slate-200 flex-1">
          <div className="flex items-baseline gap-4 mb-3">
            <h2 className="text-3xl font-serif font-black text-blue-900 uppercase tracking-tight">
              Billing Insights
            </h2>
            <span className="text-xs font-mono text-slate-500 font-bold border-l border-slate-300 pl-3">
              REV-v2.5.0
            </span>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 items-center text-left">
            <div className="space-y-1">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Billing Status</p>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 bg-emerald-600" />
                <span className="text-[11px] font-bold text-slate-700 uppercase">Ledger Synced</span>
              </div>
            </div>

            <div className="space-y-1">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">CTO Identifier</p>
              <p className="text-[11px] font-bold text-blue-900 uppercase">OM-{operatorShortID}</p>
            </div>

            <div className="space-y-1">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Organization</p>
              <p className="text-[11px] font-bold text-slate-700 uppercase">Focus NDT Group</p>
            </div>

            <div className="space-y-1">
              <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Current Session</p>
              <p className="text-[11px] font-bold text-slate-700 uppercase font-mono">
                {format(new Date(), "yyyy-MM-dd | HH:mm")} IST
              </p>
            </div>
          </div>
        </div>

        {/* PRIMARY CONTROL BLOCK */}
        <div className="bg-slate-50 p-8 flex flex-col justify-center gap-4 border-l border-slate-200 min-w-[300px]">
          <button 
            className="flex items-center justify-center gap-4 px-10 py-4 bg-blue-900 text-white font-black text-[12px] uppercase tracking-[0.2em] hover:bg-blue-950 transition-all shadow-lg active:scale-95"
          >
            <Zap size={14} className="text-amber-400" />
            Initialize Procurement
          </button>
          <p className="text-[9px] text-center text-slate-500 font-bold uppercase tracking-tighter">
            Authorization Level: CTO / Admin Required
          </p>
        </div>
      </div>

      {/* SECTION 1B: TECHNICAL FILTERS & METRICS */}
      <div className="flex flex-wrap items-center justify-between bg-slate-100/50 p-4 border-b border-slate-300">
        <div className="flex items-center gap-6">
          <div className="flex flex-col">
            <span className="text-[9px] font-black text-slate-400 uppercase mb-1">Regional Scope</span>
            <div className="flex bg-white border border-slate-300">
              {['India', 'Global'].map((r) => (
                <button 
                  key={r}
                  onClick={() => setIsIndia(r === 'India')}
                  className={`px-6 py-2 text-[10px] font-black transition-all border-r border-slate-200 last:border-r-0 ${
                    (isIndia && r === 'India') || (!isIndia && r === 'Global')
                    ? 'bg-blue-900 text-white' 
                    : 'text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {r.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="h-10 w-px bg-slate-300" />

          <div className="flex gap-8 text-left">
            <div className="flex flex-col">
              <span className="text-[9px] font-black text-slate-400 uppercase mb-1">Fiscal Cycle</span>
              <span className="text-sm font-black text-blue-900 uppercase">{viewMonth} {viewYear}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[9px] font-black text-slate-400 uppercase mb-1">Engine Health</span>
              <span className="text-sm font-black text-emerald-600 uppercase tracking-tighter italic">Operational</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button className="p-3 bg-white text-slate-600 border border-slate-300 hover:bg-slate-50 transition-all">
            <Activity size={16} />
          </button>
          <button className="flex items-center gap-3 bg-white border-2 border-blue-900 text-blue-900 px-6 py-3 text-[11px] font-black uppercase tracking-[0.1em] hover:bg-blue-50 transition-all">
            <FileDown size={16} />
            Generate Compliance Export
          </button>
        </div>
      </div>

      {/* DYNAMIC CREDIT CALCULATION PANEL */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-slate-300 border-b border-slate-300">
        <div className="p-10 bg-white">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-3">Live Ledger Balance</p>
          {isLoading ? (
            <Loader2 className="animate-spin text-blue-600" />
          ) : (
            <h3 className="text-6xl font-black text-slate-900 tracking-tighter">
              {availableCredits.toLocaleString()} <span className="text-xs font-light text-slate-400 uppercase tracking-widest italic">Existing</span>
            </h3>
          )}
        </div>

        <div className="p-10 bg-blue-50 border-x border-slate-300">
          <p className="text-[10px] font-black text-blue-600 uppercase tracking-[0.3em] mb-3">Refill Selection</p>
          <h3 className="text-4xl font-black text-blue-900 tracking-tighter">
            + {selectedPlan?.credits.toLocaleString()} <span className="text-xs font-bold uppercase ml-2">Units</span>
          </h3>
          <p className="text-[10px] text-blue-400 font-bold uppercase mt-2 italic tracking-widest">{selectedPlan?.name}</p>
        </div>

        <div className="p-10 bg-blue-900 flex flex-col justify-center relative overflow-hidden">
          <p className="text-[10px] font-black text-white/60 uppercase tracking-[0.3em] mb-3">Projected Total Capacity</p>
          <h3 className="text-6xl font-black text-white tracking-tighter italic">
            {projectedTotal.toLocaleString()} <span className="text-xs font-light text-blue-200 uppercase not-italic tracking-widest">Units</span>
          </h3>
          <ArrowUpRight size={40} className="absolute top-4 right-4 text-white/10" />
        </div>
      </section>

      {/* PROCUREMENT TABLE */}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 text-[11px] font-black text-slate-500 uppercase tracking-widest border-b border-slate-300">
              <th className="px-10 py-5 border-r border-slate-200">Engine Configuration</th>
              <th className="px-10 py-5 border-r border-slate-200 text-center">Unit Volume</th>
              <th className="px-10 py-5 border-r border-slate-200 text-right">Investment Total</th>
              <th className="px-10 py-5 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {currentTiers.map((tier) => (
              <tr 
                key={tier.id}
                onClick={() => setSelectedPlanId(tier.id)}
                className={`group cursor-pointer transition-all ${selectedPlanId === tier.id ? 'bg-blue-50' : 'hover:bg-slate-50/50'}`}
              >
                <td className="px-10 py-8 border-r border-slate-100">
                  <div className="flex items-center gap-4">
                    <div className={`w-4 h-4 border-2 transition-all ${selectedPlanId === tier.id ? 'bg-blue-900 border-blue-900' : 'bg-white border-slate-300'}`} />
                    <div>
                      <div className="text-base font-black text-slate-900 uppercase tracking-tight flex items-center gap-3">
                        {tier.name}
                        {tier.popular && <span className="bg-blue-900 text-white text-[9px] px-2 py-0.5 tracking-tighter">SYSTEM CHOICE</span>}
                      </div>
                      <div className="text-[10px] text-slate-400 font-bold uppercase mt-1 tracking-widest italic">Industrial Analysis Engine</div>
                    </div>
                  </div>
                </td>
                <td className="px-10 py-8 text-center border-r border-slate-100">
                  <span className="text-2xl font-black text-slate-900">{tier.credits.toLocaleString()}</span>
                  <span className="text-[11px] font-black text-slate-400 uppercase ml-2 tracking-widest">Units</span>
                </td>
                <td className="px-10 py-8 text-right border-r border-slate-100">
                  <div className="text-2xl font-black text-blue-900 tracking-tighter">{currentSymbol}{tier.price.toLocaleString()}</div>
                  <div className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-1">Excl. Statutory Levies</div>
                </td>
                <td className="px-10 py-8 text-right">
                  <button className={`px-8 py-3 font-black uppercase text-xs tracking-widest transition-all ${
                    selectedPlanId === tier.id ? 'bg-blue-900 text-white shadow-md' : 'bg-slate-100 text-slate-400'
                  }`}>
                    {selectedPlanId === tier.id ? 'Selected' : 'Select'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* FOOTER STATUTORY INFO */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-300 border-t border-slate-300">
        <div className="bg-white p-8 flex gap-6 items-start">
          <div className="bg-slate-100 p-4 border border-slate-200">
            <ShieldCheck size={28} className="text-blue-900" />
          </div>
          <div className="text-left">
            <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Protocol Compliance</h5>
            <ul className="space-y-4 text-[11px] text-slate-500 font-bold uppercase tracking-tight leading-relaxed">
              <li className="flex items-center gap-3">
                <ChevronRight size={14} className="text-blue-900" />
                <span>Credits synced via global master ledger (Focus NDT Group)</span>
              </li>
              <li className="flex items-center gap-3">
                <ChevronRight size={14} className="text-blue-900" />
                <span>ESIC / PF statutory compliance verified for procurement</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-white p-8 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Activity size={32} className="text-blue-600 animate-pulse" />
            <div className="text-left">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">System Identity</p>
              <p className="text-sm font-bold text-slate-900 uppercase tracking-widest mt-1">EvalNDT v2.5.0 Deployment</p>
            </div>
          </div>
          <button className="bg-slate-900 text-white px-8 py-3 font-black uppercase text-xs tracking-widest hover:bg-blue-900 transition-all">
            Support Interface
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};

export default EvalNDTBillingPortal;